// Feb 2026 — Sticky top strip that lists every open admin page as a
// pill. Multi-page tabbing lets an admin keep Orders + Stock Adjust +
// Outstanding open simultaneously without losing the filters they
// applied on each (state preservation lives in `AdminTabsShell`,
// which keeps every listed page mounted).
//
// Interactions:
//   * Click a tab      → switch to it (navigate to its path).
//   * Middle-click     → close the tab.
//   * ×  button        → close the tab.
//   * Right-click      → context menu with Close, Close others, Close all.
//   * Ctrl / Cmd click on a sidebar item → open in a NEW tab without
//     switching (handled by the shell's location watcher — this bar
//     just renders the persisted list).
//
// The Dashboard tab is `pinned` — it can be re-anchored but never
// fully closed, so `/admin` always stays reachable from every state.

import React, { useEffect, useRef, useState } from 'react';
import { X, LayoutDashboard } from 'lucide-react';

const TAB_KEY = 'admin_tabs_v1';

export const loadPersistedTabs = () => {
  try {
    const raw = localStorage.getItem(TAB_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !Array.isArray(parsed.tabs)) return null;
    return parsed;
  } catch {
    return null;
  }
};

export const persistTabs = (tabs, activeKey) => {
  try {
    localStorage.setItem(TAB_KEY, JSON.stringify({ tabs, activeKey }));
  } catch {
    /* private mode / quota — ignore */
  }
};

const AdminTabsBar = ({ tabs, activeKey, onSwitch, onClose, onCloseOthers, onCloseAll }) => {
  const [menu, setMenu] = useState(null); // { x, y, tabKey }
  const barRef = useRef(null);
  const activeTabRef = useRef(null);

  // Scroll the active tab into view when it changes (keeps the
  // currently-open tab visible after a long list has been scrolled).
  useEffect(() => {
    if (activeTabRef.current && activeTabRef.current.scrollIntoView) {
      activeTabRef.current.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
    }
  }, [activeKey]);

  // Dismiss the context menu on any outside click / escape.
  useEffect(() => {
    if (!menu) return;
    const close = () => setMenu(null);
    const onEsc = (e) => { if (e.key === 'Escape') setMenu(null); };
    window.addEventListener('click', close);
    window.addEventListener('keydown', onEsc);
    return () => {
      window.removeEventListener('click', close);
      window.removeEventListener('keydown', onEsc);
    };
  }, [menu]);

  if (!tabs || tabs.length === 0) return null;

  return (
    <div
      ref={barRef}
      className="sticky top-0 z-40 bg-white border-b border-slate-200 shadow-sm"
      data-testid="admin-tabs-bar"
      role="tablist"
      aria-label="Open admin pages"
    >
      <div className="flex items-center gap-1 overflow-x-auto px-2 py-1.5 no-scrollbar">
        {tabs.map((tab) => {
          const isActive = tab.key === activeKey;
          return (
            <button
              key={tab.key}
              ref={isActive ? activeTabRef : null}
              type="button"
              role="tab"
              aria-selected={isActive}
              data-testid={`admin-tab-${tab.key.replace(/\W+/g, '-')}`}
              onClick={() => onSwitch(tab)}
              onAuxClick={(e) => {
                // Middle-click closes the tab (like a browser).
                if (e.button === 1 && !tab.pinned) {
                  e.preventDefault();
                  onClose(tab);
                }
              }}
              onContextMenu={(e) => {
                e.preventDefault();
                setMenu({ x: e.clientX, y: e.clientY, tabKey: tab.key });
              }}
              className={
                `group inline-flex items-center gap-2 px-3 h-8 rounded-t-md border-b-2 whitespace-nowrap text-xs font-bold transition-colors flex-shrink-0 ` +
                (isActive
                  ? 'bg-blue-50 border-[#0078D4] text-[#0078D4]'
                  : 'bg-slate-50 border-transparent text-slate-600 hover:bg-slate-100 hover:text-slate-900')
              }
              title={tab.path}
            >
              {tab.pinned && <LayoutDashboard className="w-3.5 h-3.5" />}
              <span className="truncate max-w-[180px]">{tab.title}</span>
              {!tab.pinned && (
                <span
                  role="button"
                  tabIndex={-1}
                  data-testid={`admin-tab-close-${tab.key.replace(/\W+/g, '-')}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    onClose(tab);
                  }}
                  className={
                    `inline-flex items-center justify-center w-4 h-4 rounded transition-colors ` +
                    (isActive
                      ? 'text-[#0078D4] hover:bg-blue-100'
                      : 'text-slate-400 opacity-0 group-hover:opacity-100 hover:bg-slate-200 hover:text-slate-700')
                  }
                  aria-label={`Close ${tab.title}`}
                >
                  <X className="w-3 h-3" />
                </span>
              )}
            </button>
          );
        })}
      </div>

      {menu && (
        <div
          className="fixed z-50 min-w-[160px] bg-white rounded-lg shadow-lg border border-slate-200 py-1 text-sm"
          style={{ left: menu.x, top: menu.y }}
          data-testid="admin-tab-context-menu"
          onClick={(e) => e.stopPropagation()}
        >
          <button
            type="button"
            className="w-full text-left px-3 py-1.5 hover:bg-slate-50 font-medium disabled:opacity-40 disabled:cursor-not-allowed"
            onClick={() => {
              const t = tabs.find((x) => x.key === menu.tabKey);
              setMenu(null);
              if (t && !t.pinned) onClose(t);
            }}
            disabled={!!(tabs.find((x) => x.key === menu.tabKey)?.pinned)}
          >
            Close
          </button>
          <button
            type="button"
            className="w-full text-left px-3 py-1.5 hover:bg-slate-50 font-medium"
            onClick={() => {
              const t = tabs.find((x) => x.key === menu.tabKey);
              setMenu(null);
              if (t) onCloseOthers(t);
            }}
          >
            Close others
          </button>
          <button
            type="button"
            className="w-full text-left px-3 py-1.5 hover:bg-slate-50 font-medium text-rose-700"
            onClick={() => {
              setMenu(null);
              onCloseAll();
            }}
          >
            Close all
          </button>
        </div>
      )}
    </div>
  );
};

export default AdminTabsBar;
